import { Timer } from "lucide-react";
import { useMatchClock } from "@/hooks/useMatchClock";

const SIZES = {
  sm: "text-sm px-2 py-0.5 gap-1",
  md: "text-base px-3 py-1 gap-1.5",
  lg: "text-xl px-4 py-1.5 gap-2",
};

const THEMES = {
  light: {
    running: "bg-red-100 text-red-600",
    paused: "bg-gray-100 text-gray-600",
    halftime: "bg-amber-100 text-amber-700",
    iconRunning: "text-red-500",
    iconPaused: "text-gray-500",
  },
  dark: {
    running: "bg-red-500/20 text-red-400",
    paused: "bg-gray-700 text-gray-300",
    halftime: "bg-amber-500/20 text-amber-300",
    iconRunning: "text-red-400",
    iconPaused: "text-gray-400",
  },
};

const PERIOD_BADGE = {
  "1T": "bg-blue-500",
  "2T": "bg-green-500",
  TE: "bg-purple-500",
};

/**
 * Shared live-match chronometer. Every screen (Deportes, Transmisiones,
 * detalle del partido y admin) renders this component so the minute,
 * the period (1T / MT / 2T / TE) and the paused state are always identical.
 */
export const LiveClock = ({
  match,
  size = "md",
  variant = "light",
  showPeriod = true,
  className = "",
  testId,
}) => {
  const { period, display, isRunning } = useMatchClock(match);
  const theme = THEMES[variant] || THEMES.light;
  const sizeClass = SIZES[size] || SIZES.md;
  const id = testId || `live-clock-${match?.match_id || "match"}`;

  if (!match || match.status !== "live" || !period) return null;

  if (period === "MT") {
    return (
      <div
        data-testid={id}
        data-clock-period="MT"
        className={`inline-flex items-center justify-center rounded-full font-semibold ${theme.halftime} ${sizeClass} ${className}`}
      >
        <Timer className="h-4 w-4" />
        <span className="font-bold">MT</span>
        <span className="text-xs uppercase tracking-wide opacity-80">Medio tiempo</span>
      </div>
    );
  }

  return (
    <div
      data-testid={id}
      data-clock-period={period}
      className={`inline-flex items-center justify-center rounded-full ${isRunning ? theme.running : theme.paused} ${sizeClass} ${className}`}
    >
      <Timer className={`h-4 w-4 ${isRunning ? theme.iconRunning : theme.iconPaused}`} />
      {showPeriod && (
        <span className={`${PERIOD_BADGE[period]} text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none`}>
          {period}
        </span>
      )}
      <span className="font-mono font-bold tabular-nums" data-testid={`${id}-time`}>
        {display}
      </span>
      {isRunning && <span className="h-2 w-2 bg-red-500 rounded-full animate-pulse" />}
    </div>
  );
};

export default LiveClock;
